import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';
import { previewCodeService } from './previewCodeService';

export const userService = {
  async createUser(userId, userData) {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        lastSignInAt: serverTimestamp(), // Set initial sign-in time
        lastActiveAt: serverTimestamp(), // Set initial activity time
      });
    } catch (error) {
      console.error('Error creating user document:', error);
      throw error;
    }
  },

  async getUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        return { id: userSnap.id, ...userSnap.data() };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting user document:', error);
      throw error;
    }
  },

  async updateUser(userId, updates) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user document:', error);
      throw error;
    }
  },

  async checkUserExists(email) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking user existence:', error);
      throw error;
    }
  },

  async updateLastSignIn(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastSignInAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating last sign in:', error);
      throw error;
    }
  },

  // Records that the user opened the app. Uses updateDoc (not setDoc-merge) on
  // purpose: brand-new OAuth users have no doc yet (created later by the preview
  // flow), and a merge-write here would create a partial doc that falsely trips
  // the new-user check. updateDoc throws on a missing doc; the heartbeat caller
  // swallows that and retries on the next open.
  async updateLastActive(userId) {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastActiveAt: serverTimestamp(),
    });
  },

  // --- Onboarding ---

  async getOnboarding(userId) {
    try {
      const user = await this.getUser(userId);
      return user?.onboarding || null;
    } catch (error) {
      console.error('Error getting onboarding state:', error);
      return null;
    }
  },

  async updateOnboarding(userId, updates) {
    try {
      // Prefix keys with "onboarding." for Firestore dot-notation merge
      const prefixed = {};
      for (const [key, value] of Object.entries(updates)) {
        prefixed[`onboarding.${key}`] = value;
      }
      await this.updateUser(userId, prefixed);
    } catch (error) {
      console.error('Error updating onboarding state:', error);
      throw error;
    }
  },

  async validatePreviewCode(code) {
    return await previewCodeService.validatePreviewCode(code);
  },

  async redeemPreviewCode(code) {
    return await previewCodeService.redeemPreviewCode(code);
  },
};
